import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { colorPalette, fontFamily, mediaQueriesBP } from "@waytek/Utilities/variables";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    title = "title",
    description = "description",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface BlogCategoryListingStyles {
    headerWrapper?: InjectableCss;
    bodyWrapper?: InjectableCss;
    titleTypo?: TypographyPresentationProps;
    descriptionWrapper?: InjectableCss;
    descriptionTypo?: TypographyPresentationProps;
}

export const blogCategoryListingStyles: BlogCategoryListingStyles = {
    headerWrapper: {
        css: css`
            border-bottom: solid 4px ${colorPalette.common.global.industrialTaupe};
        `,
    },
    titleTypo: {
        lineHeight: "normal",
        weight: 500,
        fontFamily,
        css: css`
            font-size: 28px;
            margin-bottom: 20px;

            ${mediaQueriesBP.minMD} {
                font-size: 36px;
            }
        `,
    },
    descriptionWrapper: {
        css: css`
            margin-bottom: 15px;

            ${mediaQueriesBP.minMD} {
                max-width: 740px;
                margin-bottom: 20px;
            }
        `,
    },
    descriptionTypo: {
        lineHeight: 1.5,
        css: css`
            font-size: 14px;

            ${mediaQueriesBP.minMD} {
                font-size: 16px;
            }

            p {
                font-size: 14px;

                ${mediaQueriesBP.minMD} {
                    font-size: 16px;
                }
            }

            > * {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
};

const styles = blogCategoryListingStyles;

const BlogCategoryListing: FC<Props> = ({ id, fields, pageTitle }) => {
    return (
        <>
            <StyledWrapper {...styles.headerWrapper}>
                <Typography as="h1" variant="body" {...styles.titleTypo} data-test-selector="PageTitle">
                    {fields.title || pageTitle}
                </Typography>
                <StyledWrapper {...styles.descriptionWrapper}>
                    <Typography {...styles.descriptionTypo}>{parse(fields.description, parserOptions)}</Typography>
                </StyledWrapper>
            </StyledWrapper>

            <Zone contentId={id} zoneName="ContentBCLW01" />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BlogCategoryListing),
    definition: {
        displayName: "Blog Category Page",
        group: "Blog" as any,
        allowedContexts: ["BlogCategoryPage" as any],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Override Page Title",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "Page Description",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
